import React from 'react'
import { Controller, useFormContext, type Control, type FieldValues } from 'react-hook-form'
import { Switch, FormControlLabel, FormControl, Box, Typography, type SxProps, type Theme } from '@mui/material'

interface FormSwitchProps {
  name: string
  control?: Control<FieldValues>
  label?: string
  /**
   * Optional caption rendered under the label, styled lighter/italicized, describing the
   * instructor's actual current state (e.g. "Opted out for MKTG340-01") rather than relying on
   * the toggle's on/off position alone to convey meaning. Receives the switch's live (unsaved)
   * checked value so it updates as the user toggles it.
   */
  supportText?: (checked: boolean) => string
  defaultValue?: boolean
  checked?: boolean
  defaultChecked?: boolean
  disabled?: boolean
  required?: boolean
  sx?: SxProps<Theme>
  customStyles?: React.CSSProperties
  labelStyle?: React.CSSProperties
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
}

const FormSwitch: React.FC<FormSwitchProps> = ({
  name,
  control,
  label,
  supportText,
  defaultValue = false,
  checked,
  defaultChecked,
  disabled = false,
  required = false,
  sx,
  customStyles,
  labelStyle,
  onChange
}) => {
  const { control: contextControl } = useFormContext()

  return (
    <FormControl sx={sx} required={required}>
      <Box display="flex" alignItems="center" justifyContent="center">
        <Controller
          name={name}
          control={control ?? contextControl}
          defaultValue={defaultValue}
          render={({ field }) => {
            const isChecked = checked ?? Boolean(field.value)
            return (
              <FormControlLabel
                control={
                  <Switch
                    {...field}
                    checked={isChecked}
                    disabled={disabled}
                    defaultChecked={defaultChecked}
                    inputProps={{ 'aria-label': label }}
                    onChange={(e, checkedValue) => {
                      field.onChange(checkedValue)
                      if (onChange) onChange(e, checkedValue)
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, ...labelStyle }}>{label}</Typography>
                    {supportText && (
                      <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                        {supportText(isChecked)}
                      </Typography>
                    )}
                  </Box>
                }
                style={{
                  margin: 0,
                  paddingRight: 2
                }}
              />
            )
          }}
        />
      </Box>
    </FormControl>
  )
}

export default FormSwitch
